import React, { useState } from "react";
import { CopyIcon, CheckIcon, GlobeIcon, MessageCircleIcon, PackageIcon, TimerIcon, UserPlusIcon, CodeIcon, ShieldCheckIcon, BlocksIcon, BoxIcon } from "lucide-react";
import { widgetSnippet } from "../../../data/widgetConfig";
import { DesignSection, ControlLabel, SwitchRow, AddRow } from "../../settings/design/DesignControls";
import { ItemRow } from "../../settings/design/ItemRow";
import { PanelProps, TextInput, widgetInputCls } from "./WidgetBits";
/* --------------------------------------------------------------------- Content */
type ContentSection = 'opening' | 'questions' | 'products';
export function WidgetContent({
  widget,
  update
}: PanelProps) {
  const [open, setOpen] = useState<ContentSection | null>(null);
  const [editing, setEditing] = useState<number | null>(null);
  const [draft, setDraft] = useState('');
  const toggle = (id: ContentSection) => setOpen(o => o === id ? null : id);
  const commit = (i: number) => {
    const text = draft.trim();
    update({
      prompts: text ? widget.prompts.map((p, pi) => pi === i ? text : p) : widget.prompts.filter((_, pi) => pi !== i)
    });
    setEditing(null);
  };
  return <div className="space-y-3" data-id="element-5062" data-mp="v0mr704edb3s">
      <p className="text-[13px] text-slate-500 leading-relaxed pb-1" data-id="element-5063" data-mp="g6i5z9c4dd5d">
        What visitors read when the chat opens.
      </p>

      <DesignSection icon={MessageCircleIcon} title="Opening message" summary={widget.greeting || 'Add a greeting'} open={open === 'opening'} onToggle={() => toggle('opening')} data-id="element-5064" data-mp="l5bjh1rep45j">
        <TextInput label="First message" multiline value={widget.greeting} onChange={greeting => update({
        greeting
      })} data-id="element-5065" data-mp="1dsfgokk9ce6r" />
        <TextInput label="Hint inside the typing box" value={widget.placeholder} placeholder="Ask anything…" onChange={placeholder => update({
        placeholder
      })} data-id="element-5066" data-mp="z4azjl0usaq3" />
      </DesignSection>

      <DesignSection icon={BoxIcon} title="Suggested questions" summary={`${widget.prompts.length} shown as tappable chips`} open={open === 'questions'} onToggle={() => toggle('questions')} data-id="element-5067" data-mp="1rrxc3is26pp3">
        <p className="text-[12.5px] text-slate-500" data-id="element-5068" data-mp="26ff9rx77ek20">Tap one to edit it.</p>
        <div className="space-y-2 !mt-3" data-id="element-5069" data-mp="21hx9swb5zf65">
          {widget.prompts.map((p, i) => editing === i ? <input key={i} autoFocus value={draft} onChange={e => setDraft(e.target.value)} onBlur={() => commit(i)} onKeyDown={e => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          if (e.key === 'Escape') setEditing(null);
        }} className={widgetInputCls} data-id="element-5070" data-mp="1xehbzwncz8rv" /> : <ItemRow key={`${p}-${i}`} label={p} onEdit={() => {
          setDraft(p);
          setEditing(i);
        }} onUp={i > 0 ? () => {
          const next = [...widget.prompts];
          [next[i - 1], next[i]] = [next[i], next[i - 1]];
          update({
            prompts: next
          });
        } : undefined} onDown={i < widget.prompts.length - 1 ? () => {
          const next = [...widget.prompts];
          [next[i + 1], next[i]] = [next[i], next[i + 1]];
          update({
            prompts: next
          });
        } : undefined} onRemove={() => update({
          prompts: widget.prompts.filter((_, pi) => pi !== i)
        })} data-id="element-5071" data-mp="1ipulqzw70wlv">
                <span className="block text-[13.5px] text-dark-navy truncate py-0.5" data-id="element-5072" data-mp="3gfrp4r354at7">{p}</span>
              </ItemRow>)}
          <AddRow label="Add question" onClick={() => {
          update({
            prompts: [...widget.prompts, '']
          });
          setDraft('');
          setEditing(widget.prompts.length);
        }} data-id="element-5073" data-mp="3ped46c9el7p8" />
        </div>
      </DesignSection>

      <DesignSection icon={PackageIcon} title="Products in chat" summary={widget.showProducts ? 'Shows product cards' : 'Text answers only'} open={open === 'products'} onToggle={() => toggle('products')} data-id="element-5074" data-mp="2xnyvcug8ylkf">
        <SwitchRow title="Show product cards" desc="The assistant can answer with real cards from your catalogue." checked={widget.showProducts} onChange={showProducts => update({
        showProducts
      })} data-id="element-5075" data-mp="36hbzytnobbmc" />
      </DesignSection>
    </div>;
}

/* ------------------------------------------------------------------- Behaviour */
type BehaviourSection = 'opening' | 'leads';
export function WidgetBehaviour({
  widget,
  update
}: PanelProps) {
  const [open, setOpen] = useState<BehaviourSection | null>(null);
  const toggle = (id: BehaviourSection) => setOpen(o => o === id ? null : id);
  return <div className="space-y-3" data-id="element-5076" data-mp="1zsd541flj4dp">
      <p className="text-[13px] text-slate-500 leading-relaxed pb-1" data-id="element-5077" data-mp="2dlcgbr8cicfg">
        When the chat appears and what it asks visitors.
      </p>

      <DesignSection icon={TimerIcon} title="When it appears" summary={[widget.autoOpen ? `Opens after ${widget.autoOpenDelay}s` : 'Opens when tapped', widget.showOnMobile ? 'on mobile too' : 'desktop only'].join(' · ')} open={open === 'opening'} onToggle={() => toggle('opening')} data-id="element-5078" data-mp="28l7y9wrsh9n4">
        <SwitchRow title="Say hello automatically" desc="Opens once by itself so visitors know they can ask." checked={widget.autoOpen} onChange={autoOpen => update({
        autoOpen
      })} data-id="element-5079" data-mp="t4zfq69m1f2a" />
        {widget.autoOpen && <div data-id="element-5080" data-mp="2zusyf8h24fs0">
            <ControlLabel hint="5–10 seconds works best" data-id="element-5081" data-mp="2zu9skq4olhuc">
              Wait {widget.autoOpenDelay} seconds
            </ControlLabel>
            <input type="range" min={0} max={30} value={widget.autoOpenDelay} onChange={e => update({
          autoOpenDelay: Number(e.target.value)
        })} className="w-full accent-primary-indigo" aria-label="Seconds before it opens" data-id="element-5082" data-mp="34unpbc0u15d2" />
          </div>}
        <SwitchRow title="Show on phones" desc="Opens full screen on small screens so it stays easy to use." checked={widget.showOnMobile} onChange={showOnMobile => update({
        showOnMobile
      })} data-id="element-5083" data-mp="2h2gzkvpm3wmm" />
      </DesignSection>

      <DesignSection icon={UserPlusIcon} title="Collect contact details" summary={widget.captureLeads ? 'On — saved to your Leads' : 'Off'} open={open === 'leads'} onToggle={() => toggle('leads')} data-id="element-5084" data-mp="2776xy934lr9d">
        <SwitchRow title="Ask for a name and number" desc="Details go straight into your Leads." checked={widget.captureLeads} onChange={captureLeads => update({
        captureLeads
      })} data-id="element-5085" data-mp="5bmrbvvv7mxr" />
        {widget.captureLeads && <TextInput label="How the assistant asks" value={widget.leadPrompt} onChange={leadPrompt => update({
        leadPrompt
      })} data-id="element-5086" data-mp="3wk1iypfvfir" />}
      </DesignSection>
    </div>;
}

/* --------------------------------------------------------------------- Install */
type InstallSection = 'code' | 'domains' | 'platforms';
export function WidgetInstall({
  widget,
  update,
  handle
}: PanelProps) {
  const [open, setOpen] = useState<InstallSection | null>(null);
  const [copied, setCopied] = useState(false);
  const [adding, setAdding] = useState(false);
  const [domain, setDomain] = useState('');
  const toggle = (id: InstallSection) => setOpen(o => o === id ? null : id);
  const snippet = widgetSnippet(widget, handle);
  const addDomain = () => {
    const d = domain.trim().replace(/^https?:\/\//, '').replace(/\/$/, '');
    if (d && !widget.domains.includes(d)) update({
      domains: [...widget.domains, d]
    });
    setDomain('');
    setAdding(false);
  };
  return <div className="space-y-3" data-id="element-5087" data-mp="1dwd4erehva3b">
      <p className="text-[13px] text-slate-500 leading-relaxed pb-1" data-id="element-5088" data-mp="1hvvl1aza8z0c">
        Put the chat on your website — once, and you’re done.
      </p>

      <DesignSection icon={CodeIcon} title="Your code" summary="Copy once, paste on your site" open={open === 'code'} onToggle={() => toggle('code')} data-id="element-5089" data-mp="1mugpjmlki4ul">
        <button onClick={() => {
        navigator.clipboard?.writeText(snippet);
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1800);
      }} className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-primary-indigo text-white text-sm font-semibold hover:bg-indigo-700 transition-colors duration-150 ease-out" data-id="element-5090" data-mp="1wtebqivitmaf">
          {copied ? <CheckIcon className="w-4 h-4" data-id="element-5091" data-mp="2amncjv5ekzhc" /> : <CopyIcon className="w-4 h-4" data-id="element-5092" data-mp="2fkoweianl2tv" />}
          {copied ? 'Copied' : 'Copy code'}
        </button>
        <pre className="p-3.5 rounded-xl bg-dark-navy text-[11px] leading-relaxed text-slate-300 overflow-x-auto !mt-3" data-id="element-5093" data-mp="2ki79mkvpe47o">
          <code data-id="element-5094" data-mp="35sm6mp9ifm43">{snippet}</code>
        </pre>
        <p className="text-[12px] text-slate-500 leading-relaxed" data-id="element-5095" data-mp="30uuj07fwem5p">
          Paste it before your site’s closing <span className="font-mono" data-id="element-5096" data-mp="3eun7rx7v6sb1">&lt;/body&gt;</span>{' '}
          tag, or send it to whoever manages your website. Changes you save here appear
          automatically — no need to paste again.
        </p>
      </DesignSection>

      <DesignSection icon={ShieldCheckIcon} title="Your websites" summary={widget.domains.length ? widget.domains.join(' · ') : 'None yet — add where the chat will live'} open={open === 'domains'} onToggle={() => toggle('domains')} data-id="element-5097" data-mp="2m6zoqmm8s9qi">
        <p className="text-[12.5px] text-slate-500" data-id="element-5098" data-mp="2vx2hqmtd5nou">Only these sites can show your chat.</p>
        <div className="space-y-2 !mt-3" data-id="element-5099" data-mp="2r3q2v4ehkv9a">
          {widget.domains.map(d => <ItemRow key={d} label={d} lead={<GlobeIcon className="w-4 h-4 text-slate-400 flex-shrink-0" data-id="element-5101" data-mp="23dnf30hlubgh" />} onRemove={() => update({
          domains: widget.domains.filter(x => x !== d)
        })} data-id="element-5100" data-mp="23eey7bu0mgpv">
              <span className="block text-[13.5px] text-dark-navy truncate py-1" data-id="element-5102" data-mp="dx9xslq7ba66">{d}</span>
            </ItemRow>)}
          {adding ? <input autoFocus value={domain} onChange={e => setDomain(e.target.value)} onBlur={addDomain} onKeyDown={e => {
          if (e.key === 'Enter') addDomain();
          if (e.key === 'Escape') setAdding(false);
        }} placeholder="yourwebsite.com" className={widgetInputCls} data-id="element-5103" data-mp="iqm9jv9s7mhi" /> : <AddRow label="Add website" onClick={() => setAdding(true)} data-id="element-5104" data-mp="xnda47v6z48p" />}
        </div>
      </DesignSection>

      <DesignSection icon={BlocksIcon} title="Website builders" summary="WordPress, Shopify, Wix, Webflow" open={open === 'platforms'} onToggle={() => toggle('platforms')} data-id="element-5105" data-mp="11mm2p9ehww2o">
        <p className="text-[12.5px] text-slate-500" data-id="element-5106" data-mp="smfxeasyb2el">Using one of these? Install in one click.</p>
        <div className="grid grid-cols-2 gap-2 !mt-3" data-id="element-5107" data-mp="38e3r7xco4et6">
          {['WordPress', 'Shopify', 'Wix', 'Webflow'].map(p => <button key={p} className="px-3 py-2.5 rounded-xl bg-white ring-1 ring-slate-200 text-[13px] font-semibold text-dark-navy hover:ring-primary-indigo/50 transition-shadow duration-150 ease-out" data-id="element-5108" data-mp="3dehoqlgn86cy">
              {p}
            </button>)}
        </div>
      </DesignSection>
    </div>;
}